import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import ContractorRestModel from "../../../models/rest/contractor/contractorRestModel";
import ListItemNumberModel from "@/models/listItemNumberModel";
import VForm from "@/models/v-form";
import ListItemStringModel from "../../../models/listItemStringModel";
import { stringMaxLength } from "../../../assets/validation/common";
import { Roles } from "../../../models/emums/roles";
import { ContractorTypes } from "../../../models/emums/contractorTypes";
import CountryRestModel from "../../../models/rest/register/countryRestModel";
import ContractorSearchItemModel from "../../../models/rest/contractor/contractorSearchItemModel";

@Component
export default class BasicTab extends Vue {
    $refs!: {
        form: VForm;
    };

    contractorPayerSearch = "";

    validation = {
        customInfoToSupervisor: [stringMaxLength(this.$i18n, this.$t("contractor.specialInfo"), 2048)],
        symbol: [
            stringMaxLength(this.$i18n, this.$t("contractor.symbol"), 60),
            (value: string) => !!value || this.$t("common.validationErrorRequired")
        ],
        name: [stringMaxLength(this.$i18n, this.$t("contractor.name"), 512)],
        contractorType: [(value: string[]) => (value as string[]).length > 0 || this.$t("common.validationErrorRequired")],
        comments: [stringMaxLength(this.$i18n, this.$t("contractor.comments"), 512)],
        specialInfo: [stringMaxLength(this.$i18n, this.$t("contractor.specialInfo"), 2048)],
        isLockedAsClientComment: [stringMaxLength(this.$i18n, this.$t("contractor.comments"), 256)],
        postalCode: [
            stringMaxLength(this.$i18n, this.$t("contractor.postalCode"), 10),
            (value: string) => {
                if (this.contractor.isPrivatePerson) {
                    return !!value || this.$t("common.validationErrorRequired");
                }
                return true;
            }

        ],
        streetAndNumber: [
            (value: string) => {
                if (this.contractor.isPrivatePerson) {
                    return !!value || this.$t("common.validationErrorRequired");
                }
                return true;
            }
        ],
        country: [
            (value: string) => {
                if (this.contractor.isPrivatePerson) {
                    return !!value || this.$t("common.validationErrorRequired");
                }
                return true;
            }
        ],
        city: [
            (value: string) => {
                if (this.contractor.isPrivatePerson) {
                    return !!value || this.$t("common.validationErrorRequired");
                }
                return true;
            }
        ],
        accountManagers: [
            (value: string[]) => (value && value.length > 0) || this.$t("common.validationErrorRequired")
        ],
    };

    get contractorTypeList(): ListItemStringModel[] {
        return this.$store.direct.state.contractor.contractorTypeList;
    }

    contractorDefaultLangForTransportOrders = [
        { text: "pl", value: "pl" },
        { text: "de", value: "de" },
        { text: "en", value: "en" },
    ]

    get customerSalesStage(): ListItemNumberModel[] {
        return this.$store.direct.state.contractor.customerSalesStage;
    }

    get contractorAssets() {
        return this.$store.direct.state.contractor.contractorAssets;
    }

    get branchList(): ListItemNumberModel[] {
        return this.contractorAssets.branches;
    }

    get departmentList(): ListItemNumberModel[] {
        return this.contractorAssets.departments;
    }

    get userListSorted(): any {
        return this.contractorAssets.crmUsers?.sort((prev, next) => (prev.text > next.text ? 1 : -1));
    }

    get onlyCrmUsers(): any {
        return this.contractorAssets.onlyCrmUsers?.sort((prev, next) => (prev.text > next.text ? 1 : -1));
    }


    get accountManagersItems(): ListItemStringModel[] {
        const isContractorCreating = this.$route.params.id == undefined;

        if (this.enableAccessForCrmForOwnContractors) {
            if (!isContractorCreating) {
                return this.userListSorted.filter((u: ListItemStringModel) => u.value !== this.currentUserId);
            }
            return this.userListSorted;
        }

        if (isContractorCreating && this.contractor.typeIsClient) {
            return this.onlyCrmUsers;
        }

        return this.userListSorted;
    }

    get contractor(): ContractorRestModel {

        return this.$store.direct.state.contractor.contractor;
    }

    get contractorOrdersInLast3Months(): number {
        return this.contractor.contractorSupervisorDataModel?.senderCount ?? 0;
    }

    get contractorLastOrderDate(): string {
        return this.contractor.contractorSupervisorDataModel?.dateLastOrder ?? "-";
    }

    get contractorTurnoverInLast3Months(): string {
        const senderTurnover = this.contractor.contractorSupervisorDataModel?.senderTurnover
            ?? this.contractor.contractorSupervisorDataModel?.turnover
            ?? 0;
        const hasOtherSaleCurrencies = this.contractor.contractorSupervisorDataModel?.hasOtherSaleCurrencies
            ?? this.contractor.contractorSupervisorDataModel?.hasOtherSaleCurrencies
            ?? false;
        const saleValueCurrency = this.contractor.saleValueCurrency;
        const formattedTurnover = new Intl.NumberFormat(this.$i18n.locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(senderTurnover);

        const currencyPart = saleValueCurrency ? ` ${saleValueCurrency}` : "";
        const otherCurrenciesPart = hasOtherSaleCurrencies ? ` ${this.$t("contractor.turnoverLast3MonthsOtherCurrenciesSuffix")}` : "";

        return `${formattedTurnover}${currencyPart}${otherCurrenciesPart}`;
    }

    get contractorCarrierOrdersInLast3Months(): number {
        return this.contractor.contractorSupervisorDataModel?.carrierCount ?? 0;
    }

    get contractorCarrierTurnoverInLast3Months(): string {
        const turnover = this.contractor.contractorSupervisorDataModel?.carrierTurnover ?? 0;
        const hasOtherPurchaseCurrencies = this.contractor.contractorSupervisorDataModel?.hasOtherPurchaseCurrencies ?? false;
        const purchaseValueCurrency = this.contractor.purchaseValueCurrency;
        const formattedTurnover = new Intl.NumberFormat(this.$i18n.locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(turnover);

        const currencyPart = purchaseValueCurrency ? ` ${purchaseValueCurrency}` : "";
        const otherCurrenciesPart = hasOtherPurchaseCurrencies ? ` ${this.$t("contractor.turnoverLast3MonthsOtherCurrenciesSuffix")}` : "";

        return `${formattedTurnover}${currencyPart}${otherCurrenciesPart}`;
    }

    get contractorCarrierNcOrdersInLast3Months(): number {
        return this.contractor.contractorSupervisorDataModel?.carrierNonConformityCount ?? 0;
    }

    get countryList(): CountryRestModel[] {
        return this.$store.direct.state.contractor.contractorAssets.countries;
    }

    get allowContractorEdit(): boolean {
        return this.$store.direct.state.contractor.allowContractorEdit;
    }

    get isContractorNew(): boolean {
        return this.$store.direct.state.contractor.isContractorNew;
    }

    get currentUserId(): string {
        return this.$store.direct.state.auth.currentUserId;
    }

    get currentUserRoles(): string[] {
        return this.$store.direct.state.auth.userRoles;
    }

    get isAdmin(): boolean {
        return this.currentUserRoles.includes(Roles.Admin);
    }

    get isCrm(): boolean {
        return this.currentUserRoles.includes(Roles.Crm);
    }

    get isCrmLower(): boolean {
        return this.currentUserRoles.includes(Roles.CrmLower);
    }

    get isCrmAdmin(): boolean {
        return this.currentUserRoles.includes(Roles.CrmAdmin);
    }

    get isBranchManager(): boolean {
        return this.currentUserRoles.includes(Roles.BranchManager);
    }

    get isFullEditingDisponentAdmin(): boolean {
        return this.currentUserRoles.includes(Roles.FullEditingDisponentAdmin);
    }

    get isHigherDisponentAdmin(): boolean {
        return this.currentUserRoles.includes(Roles.HigherDisponentAdmin);
    }

    get isAccountantAdmin(): boolean {
        return this.currentUserRoles.includes(Roles.AccountantAdmin);
    }

    get isDisponent(): boolean {
        return this.currentUserRoles.includes(Roles.Disponent);
    }

    get isDisponentAdmin(): boolean {
        return this.currentUserRoles.includes(Roles.DisponentAdmin);
    }

    get disponentElevatedPrivilegesForCarriers(): boolean {
        return this.$store.direct.state.auth.disponentElevatedPrivilegesForCarriers;
    }

    get contractorTags(): ListItemNumberModel[] {
        return this.$store.direct.state.contractor.contractorAssets.contractorTags
    }

    get enableAccessForCrmForOwnContractors(): boolean {
        return this.$store.direct.state.auth.enableAccessForCrmForOwnContractors;
    }

    @Watch("contractor", { immediate: true })
    onContractorChanged() {
        this.$nextTick(function () {
            this.$refs.form.resetValidation();
        });

        if (this.contractor.contractorPayerId && this.contractor.contractorPayerNameAndSymbol) {
            this.contractorFound = [{
                value: this.contractor.contractorPayerId,
                text: this.contractor.contractorPayerNameAndSymbol,
            } as ContractorSearchItemModel];
        }
    }

    @Watch("this.$store.direct.state.contractor.contractor")
    get contractorTypeSelectedList(): string[] {
        const result = [] as string[];
        const contractor = this.$store.direct.state.contractor.contractor;

        if (contractor.typeIsCarrier) result.push(ContractorTypes.Carrier);
        if (contractor.typeIsOther) result.push(ContractorTypes.Other);
        if (contractor.typeIsPartner) result.push(ContractorTypes.Partner);
        if (contractor.typeIsClient) result.push(ContractorTypes.Client);
        if (contractor.typeIsWarehouse) result.push(ContractorTypes.Warehouse);
        if (contractor.typeIsCustomsAgency) result.push(ContractorTypes.CustomsAgency);
        if (contractor.typeIsShipowner) result.push(ContractorTypes.Shipowner);
        if (contractor.typeIsFactor) result.push(ContractorTypes.Factor);

        return result;
    }

    set contractorTypeSelectedList(value: string[]) {
        const contractor = this.$store.direct.state.contractor.contractor;
        const isClientSelected = value.indexOf(ContractorTypes.Client) > -1;

        if (this.$route.params.id == undefined && !contractor.typeIsClient && isClientSelected && !this.enableAccessForCrmForOwnContractors) {
            contractor.accountManagers = [];
        }

        contractor.typeIsCarrier = value.indexOf(ContractorTypes.Carrier) > -1;
        contractor.typeIsOther = value.indexOf(ContractorTypes.Other) > -1;
        contractor.typeIsPartner = value.indexOf(ContractorTypes.Partner) > -1;
        contractor.typeIsClient = isClientSelected;
        contractor.typeIsWarehouse = value.indexOf(ContractorTypes.Warehouse) > -1;
        contractor.typeIsCustomsAgency = value.indexOf(ContractorTypes.CustomsAgency) > -1;
        contractor.typeIsShipowner = value.indexOf(ContractorTypes.Shipowner) > -1;
        contractor.typeIsFactor = value.indexOf(ContractorTypes.Factor) > -1;
    }

    get locationIsSaved(): boolean {
        if (this.contractor.lat != null && this.contractor.lng != null) {
            return true;
        }
        return false;
    }

    get contractorFound(): ContractorSearchItemModel[] {
        return this.$store.direct.state.common.contractorSearch;
    }

    set contractorFound(val: ContractorSearchItemModel[]) {
        this.$store.direct.commit.common.setContractorSearch(val);
    }

    get userCanEditPayer(): boolean {
        return this.currentUserRoles.includes(Roles.Admin)
            || this.currentUserRoles.includes(Roles.AccountantAdmin)
            || this.currentUserRoles.includes(Roles.Accountant);
    }

    @Watch("contractorPayerSearch")
    async contractorPayerSearchLoadItems(val: string) {
        if (val) {
            await this.$store.direct.dispatch.common.searchContractors({
                searchText: val,
                contractorTypes: [],
                contractorPropertiesEnabled: [],
                source: ""
            });
        }
    }

    async created() {
        await this.$nextTick();

        if (this.contractor.contractorPayerId && this.contractor.contractorPayerNameAndSymbol) {
            this.contractorFound = [{
                value: this.contractor.contractorPayerId,
                text: this.contractor.contractorPayerNameAndSymbol,
            } as ContractorSearchItemModel];
        }
    }

    selectedContractorPayerChanged(payerId: number | null) {
        this.contractor.contractorPayerId = payerId;
    }

    fillContractorName(val: string) {
        if (this.isContractorNew == true) {
            this.contractor.name = val;
        }
    }

    validate(): number {
        let errorCount = 0;
        this.$refs.form.validate();
        this.$refs.form.inputs.forEach((input: any) => {
            if (input.validate() === false) ++errorCount;
        });
        return errorCount;
    }

    openLocationDialog() {
        this.$store.direct.dispatch.locationDialog
            .openDialog({
                showDialog: true,
                title: `${this.$t("contractor.setLocation")}`
            })
            .then(async confirm => {
                if (confirm) {
                    await this.$store.direct.dispatch.contractor.saveLocation();
                }
            });
    }

    generateKpiReportFile() {
        this.$store.direct.commit.contractor.setShowKpiGeneratingDialog(true);
    }
}
